import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Title } from "@angular/platform-browser";
import { ActivatedRoute } from '@angular/router';
import { forkJoin, from, concat, of, Subject, Subscription, catchError } from 'rxjs';
import { map, tap, concatMap, filter, switchMap, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { LazyLoadEvent, MenuItem, MessageService, FilterMetadata } from 'primeng/api';
import { Table } from 'primeng/table';
import { KeycloakService } from 'keycloak-angular';

import { saveAs } from 'file-saver';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { CliListEntry } from 'src/app/shared/cli-list-entry';
import { DocListEntry, DocTypes } from 'src/app/shared/doc-list-entry';
import { DocItems } from 'src/app/shared/doc-items';
import { DoclistQueryParams } from 'src/app/shared/doclist-query-params';
import { DocumentListViewType } from 'src/app/shared/document-list-view-type';
import { Currency } from 'src/app/shared/currency';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';
import { error } from 'ajv/dist/vocabularies/applicator/dependencies';

@Component({
  selector: 'app-documents',
  templateUrl: './documents.component.html',
  styleUrls: ['./documents.component.css'],
  encapsulation: ViewEncapsulation.None
})

/**
 * Document list page with lazy-loading and multi-criteria filtering.
 *
 * Maintains three data arrays:
 * - `datasource` — full result set loaded from the API for the selected date range.
 * - `dlTemp` — `datasource` after all active table-column filters are applied.
 * - `dlCurr` — the visible page slice of `dlTemp` (bound to the PrimeNG `Table` value).
 *
 * Lazy reload is driven by `listOptions$` (a `Subject`) piped through `debounceTime(200)` →
 * `distinctUntilChanged` → `switchMap(getDocListDateEx)`. Callers push new options via
 * `bumpListOptions()`. Fast-search strings shorter than 3 chars (but non-empty) are ignored
 * to avoid expensive partial-match queries; the user sees a warning toast when they exceed 2
 * chars because such queries can take up to 45 seconds on the server.
 *
 * Five filter-state keys are persisted to `localStorage` on `ngOnDestroy`:
 * `filterStoreName`, `depthStoreName`, `rowsStoreName`, `showColStoreName`, `showNameStoreName`.
 *
 * Initial header data (seller companies, doc types, currencies) is loaded concurrently via
 * `forkJoin`. Three bookmarks (`bookmark1/2/3Document`) let the user pin documents and
 * jump back to them from anywhere on the list.
 */
export class DocumentsComponent implements OnInit, OnDestroy {
  test: any[] = [0, 1, 2];

  globalSettinsData: GlobalSettingsData;
/*
 * TODO: 1) Date filter
 * Still need work on table date filtering as well as global date filtering on database side
 *  
 * TODO: 2) Sum/number filter
 * Need to decide how to work with enetered value (less, great, equal, etc)
 *
 * TODO: 3) Search by product - code/name/descrition/vendor????
 *
 */
  todaydate: Date = new Date();

  parentCompany: number = 0;
  mainCurrency: number = 0;

  datasource: DocListEntry[] = []; // All documents for stated period (and global filter)
  dlTemp: DocListEntry[] = [];     // All documents with table filter applied
  dlCurr: DocListEntry[] = [];     // Visible part (table's value property) of dlTemp

  selectedDocument: DocListEntry = null;
  selectedDocumentId: number = 0;

  selectedDocument2: DocListEntry = null;    // For context menu actions;

  selectedDocContent: DocItems[] = [];

  selectedDocType: number = 0;

  items: MenuItem[] = [];

  /** Controls `app-agent-bank-payment-review`'s visibility -- opened from the
   *  "Огляд банківських транзакцій" toolbar button, right after New/Delete.
   *  Same audience as `HomeComponent.canSeeFinanceResult`/its own copy of
   *  this button in the Платежи tab header (the dialog is server-gated
   *  independently too, `finance-manager` OR `portal-admin`, per
   *  `docs/PROMPT_agent-bank-payment-review-mvp.md`) -- this is just a
   *  second, more discoverable entry point into the same dialog, not a
   *  second implementation of it. */
  displayBankPaymentReview: boolean = false;

  /** Returns `true` when the user holds the `portal-admin` or
   *  `finance-manager` Keycloak role -- same check as
   *  `HomeComponent.canSeeFinanceResult`. */
  public get canSeeFinanceResult(): boolean { return (this.auth.isUserInRole('portal-admin') || this.auth.isUserInRole('finance-manager')); }

  selectedDocTypes: DocTypes[] = [];
  docTypes: DocTypes[] = [];

  selectedCurList: Currency[] = [];
  curList: Currency[] = [];

  documentGlobalFilter: string = '';

  documentNumberFilter: string = '';
  documentAuthorFilter: string = '';
  documentCompanyFilter: string = '';
  documentNote1Filter: string = '';
  documentNote2Filter: string = '';

  documentDOCSUMFilter: number = 0;
  documentPAYDSUMFilter: number = 0;
  documentSHIPSUMFilter: number = 0;

  selectedSellerCompany: CliListEntry[] = [];
  sellerCompany: CliListEntry[] = [];

  private docListQuery: Subscription = new Subscription();
  private docTypeQuery: Subscription = new Subscription();
  private curListQuery: Subscription = new Subscription();
  private selListQuery: Subscription = new Subscription();
  private _productInfoQuery: Subscription = new Subscription();
  private _prodShippedQuery: Subscription = new Subscription();
  private _doc2ListQuery: Subscription = new Subscription();

  private _deleteDocQuery: Subscription = new Subscription();
  private _childDocQuery: Subscription = new Subscription();

  totalRecords: number = 0;
  rows: number = 25;
  first: number = 0;
    
  loading: boolean = false;
  private subscriptions = new Subscription();

  filterDate: Date[] = [];
  doclistDateRange: Date[] = [];

  monthToLoad: number = 6;  // => set to 12 or better remember in initial settings

  minMonths: number = 6;
  maxMonths: number = 48;

  _filterSettings: DoclistQueryParams = null;
  tableFilterState: FilterMetadata = null;

  filterStoreName: string = 'documentListFilter';
  depthStoreName: string = 'documentListDepth';
  rowsStoreName: string = 'documentListRows';
  showColStoreName: string = 'documentShowCurCol';
  showNameStoreName: string = 'documentShowFullName';

  docStrString: string = '';
  cliStrString: string = '';
  lcoStrString: string = '';
  proStrString: string = '';

  /** Returns `true` when all fast-search strings are either empty or longer than 2 characters, allowing the query to proceed. */
  public get ifSomethingToFilter(): boolean {
    return (
      ((this.docStrString.length == 0) || (this.docStrString.length > 2)) &&
      ((this.cliStrString.length == 0) || (this.cliStrString.length > 2)) &&
      ((this.lcoStrString.length == 0) || (this.lcoStrString.length > 2)) &&
      ((this.proStrString.length == 0) || (this.proStrString.length > 2))
    );
  }

  /** Returns `true` when no row is currently selected. */
  public get ifThereNotSelection(): boolean { return !this.canMarkDocument(); }
  /** Returns `true` when the selected document has no parent (`PRIMDOCID` is falsy). */
  public get ifSelectedHasNotParent(): boolean {
    return !(this.selectedDocument && this.selectedDocument.PRIMDOCID);
  }

  bookmark1Document: DocListEntry;
  bookmark2Document: DocListEntry;
  bookmark3Document: DocListEntry;

  /** Returns `true` when bookmark slot 1 is occupied. */
  public get isThereBookmark1(): boolean { return (this.bookmark1Document != null); }
  /** Returns `true` when bookmark slot 2 is occupied. */
  public get isThereBookmark2(): boolean { return (this.bookmark2Document != null); }
  /** Returns `true` when bookmark slot 3 is occupied. */
  public get isThereBookmark3(): boolean { return (this.bookmark3Document != null); }

  /** Returns `true` when the current page (`dlCurr`) has at least one row to export. */
  public get canExport(): boolean { return (this.dlCurr || []).length > 0; }

  displayDC: boolean = false;

  displayND: boolean = false;

  /** Set from the `?requestId=` query param when arriving via the Teams Claim deep link
   *  (`#/agent-intake?requestId=<id>`) -- passed straight through to
   *  `app-create-new-document`'s `agentRequestId` input. See
   *  `docs/PROMPT_agent-intake-confirmation-panel.md`. */
  agentRequestId: number = null;

  exportDS: boolean;

  confirmDeleteDocumentOn: boolean = false;

  confirmDeleteDocumentText: string;

  typeConfirmationOnDelete: boolean = true;

  private _docSubscriptionQuery: Subscription;

  searchDocumentDialog: boolean;

  private listOptions$ = new Subject<{ parent: number, view: number, fromdate: Date, search: string, sStr1: string, sStr2: string, sStr3: string, sStr4: string }>();

  showCurrencyColumn: boolean = false;
  showFullNameColumn: boolean = true;

  selectAllTypes: boolean = false;
  selectAllCurr: boolean = false;
  selectAllCompanies: boolean = false;

  @ViewChild('dt') table: Table;

  constructor(
    private restapi: RestApiService,
    private docApi: DocumentApiService,
    private cliApi: ClientApiService,
    private messageService: MessageService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,
    private route: ActivatedRoute,
    private auth: KeycloakService,
  ) {

    this.items = [
      { label: 'Перейти к первичному', icon: 'pi pi-fw pi-directions-alt', command: () => this.moveToParentDocument(this.selectedDocument), disabled: true },
      { label: 'Показать дерево документов...', icon: 'pi pi-fw pi-sitemap', command: () => this.showDocTree(this.selectedDocument), disabled: false },
      { separator: true },
      { label: 'Создать подчиненный...', icon: 'pi pi-fw pi-copy', command: () => this.createChildDocument(this.selectedDocument) },
    //  { label: 'Скопировать в новый...', icon: 'pi pi-fw pi-clone', command: () => this.createNewDocument(this.selectedDocument) },
    //  { label: 'Открыть/Редактировать...', icon: 'pi pi-fw pi-pencil',  command: () => this.viewDocument(this.selectedDocument) },
    ];
  }

  /** Persists all five filter-state keys to `localStorage` and unsubscribes from all active subscriptions. */
  ngOnDestroy(): void {
    console.log('*** DocumentsComponent ngOnDestroy: ')
    try {
      localStorage.setItem(this.filterStoreName, JSON.stringify(this._filterSettings));
    } catch (err) {
      console.error('Failed to save filter settings:', err);
    }
    localStorage.setItem(this.depthStoreName, JSON.stringify(this.monthToLoad));
    localStorage.setItem(this.rowsStoreName, JSON.stringify(this.table.rows));
    localStorage.setItem(this.showColStoreName, JSON.stringify(this.showCurrencyColumn));
    localStorage.setItem(this.showNameStoreName, JSON.stringify(this.showFullNameColumn));

    if (this.docListQuery) { this.docListQuery.unsubscribe(); }

    if (this.docTypeQuery) { this.docTypeQuery.unsubscribe(); }

    if (this.curListQuery) { this.curListQuery.unsubscribe(); }

    if (this.selListQuery) { this.selListQuery.unsubscribe(); }

    if (this._docSubscriptionQuery) { this._docSubscriptionQuery.unsubscribe(); }

    if (this._productInfoQuery) { this._productInfoQuery.unsubscribe(); }

    if (this._prodShippedQuery) { this._prodShippedQuery.unsubscribe(); }

    if (this._doc2ListQuery) { this._doc2ListQuery.unsubscribe(); }
    if (this._deleteDocQuery) { this._deleteDocQuery.unsubscribe(); }
    if (this._childDocQuery) { this._childDocQuery.unsubscribe(); }

    this.subscriptions.unsubscribe();
  }

  /**
   * Applies all active column-filter criteria from the PrimeNG `Table` to `datasource`,
   * produces `dlTemp` (filtered set) and `dlCurr` (current page slice), then computes
   * per-row status flags (`fs`/`ps`/`fp`/`pp`/`np`/`go`/`td`/`fd`) on `dlCurr`.
   * Sets `totalRecords` to `dlTemp.length` and clears `loading`.
   */
  updateDataTable() {
    if (this.datasource) {
      console.log('*** DocumentsComponent Lazy Load initiated: datasource is valid');
      environment.production || console.table(this.table?.filters);

      this.dlTemp = this.datasource.filter(doc => {
          let fit: boolean = true;

          if (this.table?.filters) {
            for (let f in this.table.filters) {
              //environment.production || console.log('*** DocumentsComponent table filter: ' + f);
              //environment.production || console.table(this.table.filters[f]);

              let fltItem = Array.isArray(this.table.filters[f]) ? this.table.filters[f] : [this.table.filters[f]];

              //environment.production || console.log('this.table.filters[f] : ' + JSON.stringify(fltItem));

              if (Array.isArray(fltItem) && (fltItem.length > 0)) {
                for (let i = 0; i < fltItem.length; i++) {
                  let flt = fltItem[i];

                  //environment.production || console.log('table flt : ' + JSON.stringify(flt['value']));

                  if (flt && flt['value']) {

                    let fltValue = (typeof flt['value'] === 'string') ? flt['value'].trim().toLowerCase() : flt['value'];

                    switch (f) {
                      case 'DOCDATE':
                        if (doc.DOCDATE) {
                          let docDate: Date = new Date(doc.DOCDATE);
                          docDate = new Date(docDate.getFullYear(), docDate.getMonth(), docDate.getDate());

                          fit = (
                            (fltValue[0] ? (docDate >= new Date(fltValue[0])) : true) &&
                            (fltValue[1] ? (docDate <= new Date(fltValue[1])) : true));
                          //console.log('DOCDATE   : ' + doc.DOCDATE + ' is ' + fit);
                        }
                        break;

                      case 'DOCTYPEID':
                        fit = fltValue.find(e => e.DOCTYPEID == doc.DOCTYPEID);
                        break;

                      case 'LOCALCOMP':
                        fit = fltValue.find(e => e.CLIID == doc.LOCALCOMPID);
                        break;

                      case 'CURID':
                        fit = fltValue.find(e => e.CURID == doc.DOCCURRID);
                        break;

                      case 'DOCNUMBER':
                      case 'AUTHOR':
                      case 'DOCNOTE1':
                      case 'DOCNOTE2':
                        fit = doc[f] && doc[f].toLowerCase().indexOf(fltValue) !== -1;
                        break;

                      // Here we have to serach by all company name fields because
                      // Like PARTNERNAME, CLIENTSNAME, PARTNERNIKNAME
                      // And we have special field PARTNERNAMESTR for this purpose, which is not loaded from database but calculated on client side

                      case 'CLIENTSNAME':
                      case 'PARTNERNAMESTR':
                        fit = (doc[f] && (doc[f].toLowerCase().indexOf(fltValue) !== -1))
                          || ((doc.PARTNERNAME && doc.PARTNERNAME.toLowerCase().indexOf(fltValue) !== -1))
                          || ((doc.CLIENTSNAME && doc.CLIENTSNAME.toLowerCase().indexOf(fltValue) !== -1))
                          || ((doc.PARTNERNIKNAME && doc.PARTNERNIKNAME.toLowerCase().indexOf(fltValue) !== -1))
                        ;
                        break;

                      case 'DOCSUM':
                      case 'PAYDSUM':
                      case 'SHIPSUM':
                        //console.table(this.table.filters[f]);
                        //console.log('doc: \'' + doc[f] + '\' filter: \'' + fltValue + '\'');

                        let f1 = isNaN(parseFloat(fltValue)) ? 0 : parseFloat(fltValue);
                        fit = (((f1 == 0) && (doc[f] == 0)) || ((f1 > 0) && (doc[f] > f1)));

                        break;

                      case 'global':
                        fit =
                          (
                             (doc.DOCNUMBER && doc.DOCNUMBER.toLowerCase().indexOf(fltValue) !== -1)
                          || (doc.AUTHOR && doc.AUTHOR.toLowerCase().indexOf(fltValue) !== -1) 
                          || (doc.CLIENTSNAME && doc.CLIENTSNAME.toLowerCase().indexOf(fltValue) !== -1) 
                          || (doc.PARTNERNIKNAME && doc.PARTNERNIKNAME.toLowerCase().indexOf(fltValue) !== -1) 
                          || (doc.PARTNERNAME && doc.PARTNERNAME.toLowerCase().indexOf(fltValue) !== -1) 
                          || (doc.DOCNOTE1 && doc.DOCNOTE1.toLowerCase().indexOf(fltValue) !== -1) 
                          || (doc.DOCNOTE2 && doc.DOCNOTE2.toLowerCase().indexOf(fltValue) !== -1)
                          );
                        break;

                      default:
                        console.log(JSON.stringify(f));

                    }
                  }
                }
              }
              if (!fit) break;
            }
          }
          return fit;
        });

      this.dlCurr = (this.table) ? this.dlTemp.slice(this.table.first, (this.table.first + this.table.rows)) : this.dlTemp;

      let jn: Date = new Date();
      let td: Date = new Date();
      td.setHours(0, 0, 0, 0);
      let tm: Date = new Date(td);
      tm.setHours(23, 59, 59);

      this.dlCurr.forEach(dc => {
        dc.fs = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0) && (Math.abs(dc.DOCSUM - dc.SHIPSUM) < 0.02);

        dc.ps = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0) && (dc.SHIPSUM > 0) && ((dc.DOCSUM - dc.SHIPSUM) > 0.02) ;

        dc.fp = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0)  && (Math.abs(dc.DOCSUM - dc.PAYDSUM) < 0.02);

        dc.pp = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0) && (dc.PAYDSUM > 0) && (Math.abs(dc.DOCSUM - dc.PAYDSUM) > 0.02);

        dc.np = (dc.DOCTYPEID == 1) && (dc.DOCSUM > 0) && ((dc.SHIPSUM - dc.PAYDSUM) > 0.02);

        //dc.go = (dc.DOCTYPEID == 1) && (new Date(dc.DOCVALDATE) < jn) && (dc.SHIPSUM == 0) && (dc.PAYDSUM == 0);
        dc.go = (dc.DOCTYPEID == 1) && (new Date(dc.DOCVALDATE).getTime() < jn.getTime()) && (dc.SHIPSUM == 0) && (dc.PAYDSUM == 0);

        dc.td = (new Date(dc.DOCDATE) > td) && (new Date(dc.DOCDATE) < tm);

        dc.fd = (new Date(dc.DOCDATE) > tm);
      });

      this.totalRecords = this.dlTemp.length;
      this.loading = false;
    }
  }

  /** Pushes the current filter state (parent, view, date range, fast-search strings) into `listOptions$` to trigger a new API load. */
  bumpListOptions() {
    this.listOptions$.next({
      parent: this.parentCompany,
      view: DocumentListViewType.ALL,
      fromdate: this.doclistDateRange[0],
      search: this.docStrString,
      sStr1: this.docStrString.trim(),
      sStr2: this.cliStrString.trim(),
      sStr3: this.lcoStrString.trim(),
      sStr4: this.proStrString.trim()
    });
  }
      
  /**
   * Initialises date ranges and `_filterSettings`, then wires up the reactive pipeline.
   * Loads seller companies, doc types, and currencies concurrently via `forkJoin`.
   * Subscribes `listOptions$` through `debounceTime(200)` → `filter` → `switchMap`
   * (`getDocListDateEx`) and calls `bumpListOptions()` to fire the first load.
   */
  doInitAllData() {
    this.todaydate = new Date();
    let fromdate = new Date(this.todaydate.getFullYear(), this.todaydate.getMonth() - this.monthToLoad, this.todaydate.getDate(), 0);

    // Set to End of current day
    //
    // Just a moment before tomorrow begins
    // But looks like today date

    this.todaydate = new Date(this.todaydate.getFullYear(), this.todaydate.getMonth(), this.todaydate.getDate(), 23, 59, 59, 999);
    this.doclistDateRange = [...[fromdate, this.todaydate]];
    this.filterDate = [...[fromdate, this.todaydate]];

    this._filterSettings = {
      USERSID: 0,
      DATERANGE: [fromdate, this.todaydate],
      GLOBAL: '',

      // DataTable Filter Parameters
      DOCTYPEID: [],
      DOCNUMBER: '',
      DOCDATE: null,
      DOCCURRID: [],
      DOCAUTHOR: '',
      DOCCLIENT: '',
      DOCSUM: null,
      DOCPAYD: null,
      DOCSHIP: null,
      DOCNOTE1: '',
      DOCNOTE2: ''
    };

    /*
     * TODO: Rewrite to concurent load
     */

    this.selListQuery =
      // Load initial data concurrently
      this.selListQuery = forkJoin([
        this.cliApi.getCliChildList(this.parentCompany),
        this.docApi.getDocTypes(),
        this.restapi.getCurrency()
      ]).pipe(
        map(([sellers, types, currencies]) => {
          this.sellerCompany = sellers;
          this.docTypes = types;
          this.curList = currencies;
        }),
        finalize(() => console.log('*** DocumentsComponent onInit: ===> Headers lists loaded'))
      ).subscribe({
        error: (err) => this.handleError('Load initial data', err)
      });

    this.subscriptions.add(
      this.listOptions$.pipe(
        debounceTime(200),
        distinctUntilChanged(),

        // We can go far beyond this point only if filter settings are OK or filter is empty
        // Therefore there is no default load of all data with small strings

        filter(opt => {
          if ((opt.sStr1.length > 2) || (opt.sStr2.length > 2) || (opt.sStr3.length > 2) || (opt.sStr4.length > 2)) {
            this.messageService.add({
              severity: 'warn',
              summary: 'Поиск по параметрам может занимать до 45 секунд!!!',
              detail: 'В случае возникновения ошибки обновите страницу'
            });
          }
          return (
            ((opt.sStr1.length == 0) || (opt.sStr1.length > 2)) &&
            ((opt.sStr2.length == 0) || (opt.sStr2.length > 2)) &&
            ((opt.sStr3.length == 0) || (opt.sStr3.length > 2)) &&
            ((opt.sStr4.length == 0) || (opt.sStr4.length > 2))
          );
        }),

        tap(() => {
          this.titleService.setTitle('Список документов за ' + this.monthToLoad + ' месяцев');
          this.loading = true;
        }),

        switchMap((opt: any) => this.docApi.getDocListDateEx(opt.parent, opt.view, opt.fromdate, opt.sStr1, opt.sStr2, opt.sStr3, opt.sStr4).pipe(
          map(data => {

            this.datasource = data;
            // Below code is moved to api call
            // this.datasource.forEach(dc => dc.PARTNERNAMESTR = (dc.DOCCLIENTSID == dc.DOCPCOCLIID) ? dc.CLIENTSNAME : dc.PARTNERNAME + ' (' + dc.CLIENTSNAME + ')');
          }),

          finalize(() => {
            this.messageService.add({ severity: 'info', summary: 'Список документов за ' + this.monthToLoad + ' месяцев обновлен' });
            this.updateDataTable();
          })
        )),

        catchError(err => {
          this.handleError('Load documents', err);
          return of([]); // Return empty array on error
        }),

        finalize(() => this.loading = false)

      ).subscribe({
        error: error => this.handleError('Subscription error', error)
      })
    );

    //this._docSubscriptionQuery =
    //  this.listOptions$.pipe(
    //    debounceTime(200),
    //    distinctUntilChanged(),
    //    tap(() => this.titleService.setTitle('Список документов за ' + this.monthToLoad + ' месяцев')),

    //    filter(opt => {
    //      if ((opt.sStr1.length > 2) || (opt.sStr2.length > 2) || (opt.sStr3.length > 2) || (opt.sStr4.length > 2)) {
    //        this.messageService.add({
    //          severity: 'warn',
    //          summary: 'Поиск по параметрам может занимать до 45 секунд!!!',
    //          detail: 'В случае возникновения ошибки обновите страницу'
    //        });
    //        this.loading = true;
    //      }
    //      return (
    //        ((opt.sStr1.length == 0) || (opt.sStr1.length > 2)) &&
    //        ((opt.sStr2.length == 0) || (opt.sStr2.length > 2)) &&
    //        ((opt.sStr3.length == 0) || (opt.sStr3.length > 2)) &&
    //        ((opt.sStr4.length == 0) || (opt.sStr4.length > 2))
    //      );
    //    }),

    //    switchMap(opt => this.docApi.getDocListDateEx(opt.parent, opt.view, opt.fromdate, opt.sStr1, opt.sStr2, opt.sStr3, opt.sStr4).pipe(
    //      map(data => {

    //        this.datasource = data;
    //        this.datasource.forEach(dc => dc.PARTNERNAMESTR = (dc.DOCCLIENTSID == dc.DOCPCOCLIID) ? dc.CLIENTSNAME : dc.PARTNERNAME + ' (' + dc.CLIENTSNAME + ')');
    //      }),

    //      finalize(() => {
    //        this.messageService.add({ severity: 'info', summary: 'Список документов за ' + this.monthToLoad + ' месяцев обновлен' });
    //        this.updateDataTable();
    //      })
    //    ))).subscribe({
    //      next: (data) => { /* handle data */ },
    //      error: (err) => {
    //        this.loading = false;
    //        this.messageService.add({
    //          severity: 'error',
    //          summary: 'Failed to load documents',
    //          detail: err.message
    //        });
    //      }
    //    });

    this.bumpListOptions();

    console.log('*** DocumentsComponent onInit: ===> Ends')
  }

  /** Reads global settings and restores five filter-state keys from `localStorage`, then calls `doInitAllData`. */
  ngOnInit(): void {
    console.log('*** DocumentsComponent onInit: ===> Starts')

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.parentCompany = this.globalsettingsservice.getMainCompanyId();
    this.mainCurrency = this.globalsettingsservice.getMainCurrencyId();

    let _storedFilterSettings: string;

    try {
      _storedFilterSettings = localStorage.getItem(this.filterStoreName);
      if (_storedFilterSettings) { this._filterSettings = JSON.parse(_storedFilterSettings); }
    } catch (err) {
      this._filterSettings = null;
      console.error('Failed to load filter settings:', err);
    }

    try {
    _storedFilterSettings = localStorage.getItem(this.depthStoreName);
      if (_storedFilterSettings) { this.monthToLoad = JSON.parse(_storedFilterSettings); }
    } catch (err) {
      this.monthToLoad = 6;
      console.error('Failed to load depth settings:', err);
    }

    try {
      _storedFilterSettings = localStorage.getItem(this.rowsStoreName);
      if (_storedFilterSettings) { this.rows = JSON.parse(_storedFilterSettings); }
    } catch (err) {
      this.rows = 25;
      console.error('Failed to load rows settings:', err);
    }

    try {
      _storedFilterSettings = localStorage.getItem(this.showColStoreName);
      if (_storedFilterSettings) { this.showCurrencyColumn = JSON.parse(_storedFilterSettings); }
    } catch (err) {
      this.showCurrencyColumn = false;
      console.error('Failed to load show currency column settings:', err);
    }

    try {
      _storedFilterSettings = localStorage.getItem(this.showNameStoreName);
      if (_storedFilterSettings) { this.showFullNameColumn = JSON.parse(_storedFilterSettings); }
    } catch (err) {
      this.showFullNameColumn = false;
      console.error('Failed to load show full name column settings:', err);
    }

    this.doInitAllData();

    // Teams Claim deep link (`#/agent-intake?requestId=<AgentRequests.id>`) resolves to
    // this same route/component -- see docs/PROMPT_agent-intake-confirmation-panel.md.
    const requestIdParam = parseInt(this.route.snapshot.queryParamMap.get('requestId')) || 0;
    if (requestIdParam) {
      this.selectedDocumentId = null;
      this.agentRequestId = requestIdParam;
      this.displayND = true;
    }
  }

  /** Handles the PrimeNG `Table` lazy-load event (sort, filter, paginate) by calling `updateDataTable`. */
  loaddl(event: LazyLoadEvent) {
    if (!this.datasource) {
      return; // Or show loading state
    }
    this.updateDataTable();
  }

  /** Applies the selected doc-type filter to the table when the multi-select changes. */
  onDocTypeChange(event) {
    console.log('*** DocumentsComponent onDocTypeChange: ' + JSON.stringify(event));
    //environment.production || console.log('*** DocumentsComponent onDocTypeChange: ' + JSON.stringify(this.selectedDocTypes));

    environment.production || console.table(this.selectedDocTypes);
    this.table.filter(this.selectedDocTypes, 'DOCTYPEID', 'in');
  }

  /** Selects or clears all doc types when the "all" checkbox changes. */
  onAllDocTypeChange(event) {
    console.log('*** DocumentsComponent onAllDocTypeChange: ' + JSON.stringify(event));
    //environment.production || console.log('*** DocumentsComponent onAllDocTypeChange: ' + JSON.stringify(this.selectedDocTypes));

    this.selectAllTypes = event.checked;
    this.selectedDocTypes = this.selectAllTypes ? [...this.docTypes] : [];

    environment.production || console.table(this.selectedDocTypes);
  //  this.table.filter(this.selectedDocTypes, 'DOCTYPEID', 'in');
  }

  /** Applies the selected seller-company filter to the table. */
  onSellerChange(event) {
//    console.log('*** DocumentsComponent onSellerChange: ' + JSON.stringify(event));
    this.table.filter(event.value, 'LOCALCOMP', 'in');
  }

  /** Selects or clears all seller companies when the "all" checkbox changes. */
  onAllSellerChange(event) {
//    console.log('*** DocumentsComponent onSellerChange: ' + JSON.stringify(event));
    this.selectedSellerCompany = event.checked ? [...this.sellerCompany] : [];
    this.selectAllCompanies = event.checked;
    this.table.filter(this.selectedSellerCompany, 'LOCALCOMP', 'in');
  }

  /** Applies the selected currency filter to the table. */
  onCurrListChange(event) {
//    console.log('*** DocumentsComponent onCurrListChange: ' + JSON.stringify(event));
    this.table.filter(event.value, 'CURID', 'in');
  }

  /** Selects or clears all currencies when the "all" checkbox changes. */
  onAllCurrListChange(event) {
    //    console.log('*** DocumentsComponent onCurrListChange: ' + JSON.stringify(event));
    this.selectedCurList = event.checked ? [...this.curList] : [];
    this.selectAllCurr = event.checked;

    this.table.filter(this.selectedCurList, 'CURID', 'in');
  }

  /** Applies `filterDate` as a `between` filter on `DOCDATE`. */
  onDateRangeChange(event) {
    //console.log('*** DocumentsComponent onDateRangeChange: ' + JSON.stringify(event));
    //console.log('*** DocumentsComponent onDateRangeChange: ' + JSON.stringify(this.filterDate));
    this.table.filter(this.filterDate, 'DOCDATE', 'between');
  }

  /** Resets `filterDate` to the full `doclistDateRange` and re-applies the date filter. */
  onDateRangeClear(event) {
    //console.log('*** DocumentsComponent onDateRangeClear: ' + event.value);
    this.filterDate = [... this.doclistDateRange];
    this.table.filter(this.filterDate, 'DOCDATE', 'between');
  }

  /** Sets `filterDate` start to the beginning of today and keeps the existing upper bound. */
  onTodayClick(event) {
    //console.log('*** DocumentsComponent onDateRangeClear: ' + event.value);

    let fromdate = new Date();
    fromdate = new Date(fromdate.getFullYear(), fromdate.getMonth(), fromdate.getDate(), 0); // Start of current day

    this.filterDate = [... [fromdate, this.doclistDateRange[1] ]];
    this.table.filter(this.filterDate, 'DOCDATE', 'between');
  }

  /** Captures `selectedDocumentId` when the user clicks a table row. */
  onRowSelect(event) {
    console.log('*** DocumentsComponent onRowSelect: type ' + event.type);

    this.selectedDocumentId = event.data.DOCID;
  }

  /** Captures `selectedDocumentId` and `selectedDocType` when a row expansion starts. */
  onRowExpanding(event) {
    //console.log('*** DocumentsComponent onRowExpanding: ' + JSON.stringify(event.data))

    this.selectedDocumentId = event.data.DOCID;
    this.selectedDocType = event.data.DOCTYPEID;
  }

  /** Clears `selectedDocType` and `selectedDocumentId` when the user deselects a row. */
  onRowUnselect(event) {
    //console.log('*** DocumentsComponent onRowUnselect: ' + JSON.stringify(event.data));

    this.selectedDocType =
    this.selectedDocumentId = null;
  }

  /** Captures the current table filter metadata for `_filterSettings` persistence. */
  handleFilter(e) {
    //console.log('*** DocumentsComponent handleFilter: ' + JSON.stringify(e));
    this.tableFilterState = e.filters;
  }

  /** `app-create-new-document`'s `(displayChange)` handler -- clears `agentRequestId` on
   *  close so a later manual "New" click doesn't reopen on a stale AI Assist tab. */
  onDisplayNDChange(v: boolean): void {
    this.displayND = v;
    if (!v) { this.agentRequestId = null; }
  }

  /** Opens the create-child-document dialog for the given parent document. */
  createChildDocument(Document: DocListEntry) {

    this.selectedDocumentId = Document.DOCID;
    if (this.selectedDocumentId) {
      this.displayND = true;
    }
  }

  /** Snapshots the current date range and global filter into `_filterSettings` and nulls all column-level filter fields (called after any filter change). */
  updateDlFilterStatus(event, n: string, m: string) {
    this._filterSettings.DATERANGE[0] = this.doclistDateRange[0];
    this._filterSettings.DATERANGE[1] = this.doclistDateRange[1];

    this._filterSettings.GLOBAL = this.documentGlobalFilter;

    this._filterSettings.DOCTYPEID = null;
    this._filterSettings.DOCNUMBER = null;
    this._filterSettings.DOCDATE = null;
    this._filterSettings.DOCCURRID = null;
    this._filterSettings.DOCAUTHOR = null;
    this._filterSettings.DOCCLIENT = null;
    this._filterSettings.DOCSUM = null;
    this._filterSettings.DOCPAYD = null;
    this._filterSettings.DOCSHIP = null;
    this._filterSettings.DOCNOTE1 = null;
    this._filterSettings.DOCNOTE2 = null;

//    console.log('*** DocumentsComponent updateDlFilterStatus documentListFilter: ' + JSON.stringify(this._filterSettings));
  }

  /** Called by PrimeNG when the table saves its state; currently a stub (date handling is complex and intentionally deferred). */
  onStateSave(state) {
//    console.log('*** DocumentsComponent onStateSave: ' + JSON.stringify(state));

    // TODO: Reset filterDate
    //
    // Here could be very compalex algoritm to remeber the today date along with filter
    // And in case the saved today date doesn't match current - reset the filterDate
    // Very tricky

  }

  /**
   * Restores filter field bindings from the PrimeNG table's saved state. The `DOCDATE`
   * filter is reset if the stored "today" date is older than the current date (day rollover
   * guard) to prevent hiding newly created documents after midnight.
   */
  onStateRestore(state) {
    // console.log('*** DocumentsComponent onStateRestore: ' + JSON.stringify(state));
    if (state.filters) {
      for (let f in state.filters) {
        environment.production || console.log('*** DocumentsComponent onStateRestore state.filters[' + f + ']: ' + JSON.stringify(state.filters[f]));
        let fltItem = state.filters[f];
        let fltValue = Array.isArray(fltItem) ? fltItem[0].value : fltItem.value;

        switch (f) {
          case 'DOCDATE':
          case 'DATERANGE':
            // Do not remeber the filtered dates
            // Becase next day you wouldn't see new documents

            let actualTodayDate = new Date();
            actualTodayDate = new Date(actualTodayDate.getFullYear(), actualTodayDate.getMonth(), actualTodayDate.getDate(), 23, 59, 59, 999);

            this.filterDate = (actualTodayDate > this.todaydate) ? [] : [...fltValue];

            if (this.filterDate.length == 0) {
              this.messageService.add({ severity: 'info', summary: 'Переход в следующие сутки', detail: 'Фильтр по дате сброшен в исходное состояние' });
            }

            break;

          case 'DOCTYPEID':
            this.selectedDocTypes = fltValue;
            break;

          case 'LOCALCOMP': 
            this.selectedSellerCompany = fltValue;
            break;

          case 'CURID':
            this.selectedCurList = fltValue;
            break;

          case 'DOCNUMBER':
            this.documentNumberFilter = fltValue;
            break;

          case 'AUTHOR':
            this.documentAuthorFilter = fltValue;
            break;

          case 'CLIENTSNAME':
          case 'PARTNERNAMESTR':
            this.documentCompanyFilter = fltValue;
            break;

          case 'DOCNOTE1':
            this.documentNote1Filter = fltValue;
            break;

          case 'DOCSUM':
            this.documentDOCSUMFilter = fltValue;
            break;

          case 'PAYDSUM':
            this.documentPAYDSUMFilter = fltValue;
            break;

          case 'SHIPSUM':
            this.documentSHIPSUMFilter = fltValue;
            break;

          case 'DOCNOTE2':
            this.documentNote2Filter = fltValue;
            break;

          case 'global':
            this.documentGlobalFilter = fltValue;
            break;

          default:
            environment.production || console.log(JSON.stringify(f));
        }
      }
    }

    if (state.rows) {
      this.rows = state.rows;
    }

    if (state.selection) {
      this.selectedDocument = state.selection;
      //console.log('*** DocumentsComponent onStateRestore: state.selection ===> ' + JSON.stringify(this.selectedDocument));
    }

    for (let doc in state.expandedRowKeys) {
      if (state.expandedRowKeys[doc]) {
        //console.log('*** DocumentsComponent onStateRestore: expandedRowKeys ===> ' + JSON.stringify(state.expandedRowKeys[doc]));
      //  this.doLoadDataItemsByID(Number(doc));
      }   
    }
  }

  /** Resets all filter strings, selections, and date range to defaults and calls `table.clearState()`, `table.reset()`, and `table.filters = {}`. */
  clearFilterStates() {
    this.docStrString = '';
    this.cliStrString = '';
    this.lcoStrString = '';
    this.proStrString = '';

    this.documentGlobalFilter = '';
    this.documentNumberFilter = '';
    this.documentAuthorFilter = '';
    this.documentCompanyFilter = '';
    this.documentNote1Filter = '';
    this.documentNote2Filter = '';
    this.documentDOCSUMFilter = null;
    this.documentPAYDSUMFilter = null;
    this.documentSHIPSUMFilter = null;
    this.selectedDocTypes = [];
    this.selectedCurList = [];
    this.selectedSellerCompany = [];
    this.filterDate = this.doclistDateRange;
    this.table.clearState();
    this.table.reset();
    //    this.table.clear();
    this.table.filters = {};

    this.rows = 25;
}

  /** Clears all filter state and triggers a fresh document reload. */
  clearOrResetTableFilter(event) {
    console.log('*** DocumentsComponent clearOrResetTableFilter: Hey! I call you! ' + JSON.stringify(event));
    this.clearFilterStates();

    this.bumpListOptions();
  }

  /** Forces a full document list reload by calling `bumpListOptions`. */
  onReloadAllData(event) {
    console.log('*** DocumentsComponent onReloadAllData: ' + JSON.stringify(event));

    this.bumpListOptions();
  }

  /** Updates the date range as the depth slider moves and triggers a new list load. */
  onSlideChange(event) {
    //console.log('*** DocumentsComponent onSlideChange: ' + event.value)
    let md: Date = new Date();
    md = new Date(md.getFullYear(), md.getMonth() - this.monthToLoad, md.getDate(), 0);

    this.doclistDateRange = [md, this.todaydate];

    this.filterDate = [... this.doclistDateRange];

    this.bumpListOptions();
  }

  /** Commits the final slider value on release and triggers a new list load. */
  onSlideEnd(event) {
    //console.log('*** DocumentsComponent onSlideEnd: ' + event.value)
    let md: Date = new Date();
    md = new Date(md.getFullYear(), md.getMonth() - this.monthToLoad, md.getDate(), 0);

    this.doclistDateRange = [md, this.todaydate];

    this.filterDate = [... this.doclistDateRange];

    this.bumpListOptions();
  }

  /** Returns `true` when a row is selected, enabling bookmark and context-menu actions. */
  canMarkDocument(): boolean {
    return (this.selectedDocument) ? true : false;
  }

  /** Toggles bookmark 1: if already set, scrolls to the bookmarked document and clears it; otherwise pins the current selection. */
  markBookmark1($event) {
    if (this.bookmark1Document) {
      console.log('*** DocumentsComponent markBookmark1: (moving) ' + this.selectedDocument.DOCID);
      this.scrollToDocument(this.bookmark1Document.DOCID)
      this.bookmark1Document = null;
    } else if (this.selectedDocument) {
      this.bookmark1Document = this.selectedDocument;
      console.log('*** DocumentsComponent markBookmark1: (marking) ' + this.bookmark1Document.DOCID);
    }
  }

  /** Toggles bookmark 2: if already set, scrolls to the bookmarked document and clears it; otherwise pins the current selection. */
  markBookmark2($event) {
    if (this.bookmark2Document) {
      console.log('*** DocumentsComponent markBookmark2: (moving) ' + this.bookmark2Document.DOCID);
      this.scrollToDocument(this.bookmark2Document.DOCID)
      this.bookmark2Document = null;
    } else if (this.selectedDocument) {
      this.bookmark2Document = this.selectedDocument;
      console.log('*** DocumentsComponent markBookmark2: (marking) ' + this.bookmark2Document.DOCID);
    }
  }

  /** Toggles bookmark 3: if already set, scrolls to the bookmarked document and clears it; otherwise pins the current selection. */
  markBookmark3($event) {
    if (this.bookmark3Document) {
      console.log('*** DocumentsComponent markBookmark3: (moving) ' + this.bookmark3Document.DOCID);
      this.scrollToDocument(this.bookmark3Document.DOCID)
      this.bookmark3Document = null;
    } else if (this.selectedDocument) {
      this.bookmark3Document = this.selectedDocument;
      console.log('*** DocumentsComponent markBookmark3: (marking) ' + this.bookmark3Document.DOCID);
    }
  }

  /** Enables or disables the "move to parent" context-menu item based on whether the selected document has a parent. */
  onShowComtextMenu(event) {
    this.items[0].disabled = !this.isDocHasParent(this.selectedDocument);
  }

  /** Returns `true` when the selected document has a non-null `PRIMDOCID`. */
  isDocHasParent(doc: DocListEntry): boolean {
    return (this.selectedDocument && (this.selectedDocument.PRIMDOCID != null));
  }

  /** Returns `true` when the document has a parent and that parent is present in the current filtered view (`dlTemp`). */
  isDocParentVisible(doc: DocListEntry): boolean {
    return (this.isDocHasParent(doc) && (this.dlTemp.findIndex((e) => { return (e.DOCID == doc.PRIMDOCID); }) >= 0));
  }

  /**
   * Paginates the table to the page containing `docId` and sets `selectedDocument` to
   * that row. Shows a warning toast if the document is not found in `dlTemp` (i.e. filtered out).
   */
  scrollToDocument(docId: number) {
    console.log('*** DocumentsComponent scrollToDocument: entering with index: ' + docId);
    let index = this.dlTemp.findIndex((e) => { return (e.DOCID == docId); })

    if (index >= 0) {
      console.log('*** DocumentsComponent scrollToDocument: find index: ' + index);
      console.log('*** DocumentsComponent scrollToDocument: find page: ' + Math.trunc(index / this.rows));
      this.table.first = this.rows * Math.trunc(index / this.rows);
      this.dlCurr = this.dlTemp.slice(this.table.first, (this.table.first + this.table.rows));
      this.selectedDocument = this.dlTemp[index];
      console.log('*** DocumentsComponent scrollToDocument: selection: ' + JSON.stringify(this.selectedDocument));
    } else {
      this.messageService.add({ severity: 'warn', summary: 'Документ не найден', detail: 'Измените настройки фильтра' });
    }
  }

  /** Button handler that scrolls to the parent of the currently selected document. */
  moveToParentDocumentBtn(event) {
    console.log('*** DocumentsComponent moveToParentDocumentBtn of selection: ' + JSON.stringify(event));
    if (this.selectedDocument && this.selectedDocument.PRIMDOCID) {
      console.log('*** DocumentsComponent moveToParentDocumentBtn looking for : ' + this.selectedDocument.PRIMDOCID);
      this.scrollToDocument(this.selectedDocument.PRIMDOCID);
    }
  }

  /** Context-menu handler that scrolls to the parent of the given document. */
  moveToParentDocument(doc: DocListEntry) {
    console.log('*** DocumentsComponent moveToParentDocument of selection: ' + doc.DOCID);
    console.log('*** DocumentsComponent moveToParentDocument looking for : ' + doc.PRIMDOCID);

    this.scrollToDocument(doc.PRIMDOCID);
  }

  /** Opens the document-tree dialog for the given document, setting `selectedDocument2` as the tree root. */
  showDocTree(doc: DocListEntry) {
    console.log('*** DocumentsComponent showDocTree of selection: ' + doc.DOCID);

    this.selectedDocument2 = this.selectedDocument;
    this.displayDC = true;
  }

  /** Scrolls to and selects the document chosen in the document-tree dialog. */
  onSelectDocumentTreeNode(event) {
    console.log('*** DocumentsComponent onSelectDocumentTreeNode: ' + JSON.stringify(event))
    this.scrollToDocument(event.DOCID);
  }

  /** Generic button-click stub (logging only). */
  buttonClick(event) {
    console.log('*** DocumentsComponent buttonClick: ' + JSON.stringify(event))
  }

  /** Opens the document-tree dialog for the currently selected document. */
  showDoctreeButtonClick(event) {
    console.log('*** DocumentsComponent buttonClick: ' + JSON.stringify(event))
    this.showDocTree(this.selectedDocument);
  }

  /**
   * Executes confirmed document deletion via `deleteDocumentById`, then resets selection
   * state and triggers a reload. Shows an info toast on success; errors are forwarded to
   * `handleError`.
   */
  onDeleteDocumentConfirmation(event) {
    console.log('*** DocumentsComponent onDeleteDocumentConfirmation: ' + JSON.stringify(event))

    this._deleteDocQuery =
      this.docApi.deleteDocumentById(this.selectedDocument.DOCID).pipe(
        catchError(err => {
          this.handleError('Delete document', err);
          return of(null); // Return null on error
        }),

        finalize(() => {
          console.log('*** DocumentsComponent onDeleteDocumentConfirmation: Delete document request completed');
          this.confirmDeleteDocumentOn = false;
          this.selectedDocumentId = null;
          this.selectedDocument = null;
          this.selectedDocType = null;
          this.messageService.add({ severity: 'info', summary: 'Документ удален', detail: 'Документ ' + this.selectedDocument.DOCNUMBER + ' успешно удален' });
          this.bumpListOptions();
        })

    ).subscribe(() => {
      error: (err) => this.handleError('Delete document subscription', err)
      });
  }

  /**
   * Guards deletion by checking `getChildDocList`. If the document has no children, opens
   * the confirmation dialog; otherwise shows an error toast explaining that documents with
   * children cannot be deleted.
   */
  deleteDocumentClick(event) {
    console.log('*** DocumentsComponent deleteDocumentClick: ' + JSON.stringify(event))

    this._childDocQuery =
      this.docApi.getChildDocList(this.selectedDocument.DOCID).pipe(map(doc => {
        if (doc.length == 0) {
          this.typeConfirmationOnDelete = environment.production;
          this.confirmDeleteDocumentText = 'DELETE ' + this.selectedDocument.DOCNUMBER;

          this.confirmDeleteDocumentOn = true;
        } else {
          this.messageService.add({ severity: 'error', summary: 'Этот документ имеет подчиненные - удалить невозможно.' });
        }
      })).subscribe(
      );
  }

  /** Opens the advanced document-search dialog. */
  doDeepSearch(event) {
    console.log('*** DocumentsComponent doDeepSearch: ' + JSON.stringify(event))

    this.searchDocumentDialog = true;
  }

  /** Logs the toggle state of the optional currency column (visibility is bound directly via `showCurrencyColumn`). */
  onShowCurrencyColumnChange(event) {
    console.log('*** DocumentsComponent onShowCurrencyColumnChange: ' + JSON.stringify(event));
    console.log('*** DocumentsComponent onShowCurrencyColumnChange: ' + JSON.stringify(this.showCurrencyColumn));
  }

  /** Logs the toggle state of the optional full-name column (visibility is bound directly via `showFullNameColumn`). */
  onShowFullNameColumnChange(event) {
    console.log('*** DocumentsComponent onShowFullNameColumnChange: ' + JSON.stringify(event))
    console.log('*** DocumentsComponent onShowCurrencyColumnChange: ' + JSON.stringify(this.showFullNameColumn));
  }

  /** Clears all four fast-search strings and triggers a fresh document reload. */
  doResetFastSearch(event) {
    console.log('*** DocumentsComponent doResetFastSearch: ' + JSON.stringify(event))

    this.docStrString = '';
    this.cliStrString = '';
    this.lcoStrString = '';
    this.proStrString = '';

    this.bumpListOptions();
  }

  /** Triggers a document reload with the current fast-search strings. */
  doFastSearch(event) {
    console.log('*** DocumentsComponent doFastSearch: ' + JSON.stringify(event))

    this.bumpListOptions();
  }

  /** Sets `exportDS = true` when a document-save event is received (stub — not yet fully implemented). */
  onDocumentSave(event) {
    console.log('*** DocumentsComponent doFastSearch: ' + JSON.stringify(event))
    this.exportDS = true;
  }

  /** Dynamically imports `file-saver` and triggers a browser download of `buffer` as an `.xlsx` file. */
  saveAsExcelFile(buffer: any, fileName: string): void {
    import("file-saver").then(FileSaver => {
      let EXCEL_TYPE =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      let EXCEL_EXTENSION = ".xlsx";
      const data: Blob = new Blob([buffer], {
        type: EXCEL_TYPE
      });

      saveAs(
        data,
        fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
      );
    });
  }

  /** Exports all visible rows in `dlCurr` to an `.xlsx` file with Ukrainian column headers via dynamic `xlsx` import. */
  onExportData() {
    console.log('*** DocumentsComponent onExportData: ')

    import("xlsx").then(xlsx => {

      let exportData: any =
        this.dlCurr.map(e => ({
          'Документ': e.DOCNAME,
          'Номер': e.DOCNUMBER,
          'Дата': new Date(e.DOCDATE),
          'Покупатель': e.CLIENTSNAME,
          'Ответственный': e.PERSONNAME,
          'Продавец': e.LOCALCOMP,
          'Менеджер': e.AUTHOR,
          'Сумма': e.DOCSUM,
          'Оплачено': e.PAYDSUM,
          'Отгружено': e.SHIPSUM,
          'Валюта': e.CURSTR,
          'Курс': e.DOCRATEVAL,
          'Примечание продавца': e.DOCNOTE1,
          'Примечание покупателя': e.DOCNOTE2,

        }));

      const worksheet = xlsx.utils.json_to_sheet(exportData || []);
      const workbook = { Sheets: { documents: worksheet }, SheetNames: ['documents'] };

      const excelBuffer: any = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array"
      });

      this.saveAsExcelFile(excelBuffer, "documents");
    });
  }

  /** Logs the error, clears `loading`, and shows a PrimeNG error toast with the operation name and message. */
  private handleError(operation: string, error: any): void {
    console.error(`${operation} failed:`, error);
    this.loading = false;
    this.messageService.add({
      severity: 'error',
      summary: `${operation} Failed`,
      detail: error.message || 'Unknown error'
    });
  }
}

